import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * The Bills page summary tiles, built from the server's figures.
 *
 * Kept out of the component so the interesting part — which tile can be answered, and what it says
 * when it cannot — is testable without rendering anything.
 */

/** Shown in place of a figure that is not available. */
export const UNAVAILABLE = '—';

/**
 * A tile is in exactly one of three states, and they are not two.
 *
 * `loading` also shows a dash, but must NOT be greyed as unavailable: the figure is on its way, and
 * styling it as absent makes every reload look like a page full of broken tiles.
 */
export const TILE_STATE = {
  loading: 'loading',
  available: 'available',
  unavailable: 'unavailable'
};

/** "48s", "3m 48s", "1h 04m" — the shape the design uses. */
export const formatDuration = seconds => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return UNAVAILABLE;
  const total = Math.max(0, Math.round(Number(seconds)));
  if (total < 60) return "".concat(total, "s");
  const minutes = Math.floor(total / 60);
  if (minutes < 60) {
    const remainder = total % 60;
    return remainder === 0 ? "".concat(minutes, "m") : "".concat(minutes, "m ").concat(remainder, "s");
  }
  const hours = Math.floor(minutes / 60);
  return "".concat(hours, "h ").concat(String(minutes % 60).padStart(2, '0'), "m");
};
export const formatPercent = value => value === null || value === undefined || Number.isNaN(Number(value)) ? UNAVAILABLE : "".concat(Math.round(value), "%");

/** A usable figure, or null. An older server can answer without a field, and undefined is not zero. */
const figure = value => typeof value === 'number' && Number.isFinite(value) ? value : null;
const plural = (count, word) => "".concat(count, " ").concat(word).concat(count === 1 ? '' : 's');
const tile = (label, value, hint) => value === null || value === undefined ? {
  label,
  value: UNAVAILABLE,
  hint,
  state: TILE_STATE.unavailable
} : {
  label,
  value: String(value),
  hint,
  state: TILE_STATE.available
};

/**
 * The seven tiles LB-5839 specifies, in the order the page reads them: what needs a person, what got
 * done, what went wrong, how fast, the two facts about intake, and how much the pipeline placed on its
 * own (LB-5978). Six fit the first view; the row scrolls for the rest.
 *
 * Auto-attached is a share of recently processed bills that carried the placement stamp, so it says
 * nothing until such a bill exists: bills processed before the stamp shipped are left out of it, as
 * the server leaves them out of the denominator.
 */
export const buildSummaryTiles = function (summary) {
  let isLoading = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  if (!summary) {
    const state = isLoading ? TILE_STATE.loading : TILE_STATE.unavailable;
    const hint = isLoading ? '' : 'Not available';
    return ['Needs review', 'Processed this month', 'Processing errors', 'Avg extraction time', 'Allowed senders', 'Rejected sender', 'Auto-attached'].map(label => ({
      label,
      value: UNAVAILABLE,
      hint,
      state
    }));
  }
  const extraction = summary.extractionTime;
  const autoAttached = figure(summary.autoAttachedPercent);
  return [_objectSpread(_objectSpread({}, tile('Needs review', summary.needsReviewCount, 'Match these to a property')), {}, {
    hint: summary.needsReviewCount === 0 ? 'Nothing waiting' : 'Match these to a property'
  }), tile('Processed this month', summary.processedThisMonthCount, autoAttached === null ? 'Arrived this month and done' : "".concat(formatPercent(autoAttached), " auto-attached")), tile('Processing errors', summary.failedCount, 'Could not be read this month'),
  // The object being present is not the same as it carrying an average: a partial payload would
  // otherwise render a dash styled as a perfectly good answer.
  figure(extraction === null || extraction === void 0 ? void 0 : extraction.averageSeconds) !== null ? {
    label: 'Avg extraction time',
    value: formatDuration(extraction.averageSeconds),
    hint: figure(extraction.medianSeconds) === null || figure(extraction.sampleSize) === null ? 'Across bills processed recently' : "Median ".concat(formatDuration(extraction.medianSeconds), " of ").concat(plural(extraction.sampleSize, 'bill')),
    state: TILE_STATE.available
  } : {
    label: 'Avg extraction time',
    value: UNAVAILABLE,
    hint: 'Nothing processed recently',
    state: TILE_STATE.unavailable
  }, tile('Allowed senders', summary.allowedSendersCount,
  // A domain count that did not arrive is not a count of nought.
  figure(summary.allowedSenderDomainCount) === null ? 'Currently trusted' : "Across ".concat(plural(summary.allowedSenderDomainCount, 'domain'))), _objectSpread(_objectSpread({}, tile('Rejected sender', summary.rejectedSenderCount, 'Awaiting approval')), {}, {
    // Built only from a figure that arrived: without the guard a missing count renders the hint
    // "undefined bills awaiting approval", which is worse than saying nothing.
    hint: figure(summary.rejectedCount) === null ? 'Awaiting approval' : summary.rejectedCount === 0 ? 'Nothing awaiting approval' : "".concat(plural(summary.rejectedCount, 'bill'), " awaiting approval")
  }), autoAttached === null ? {
    label: 'Auto-attached',
    value: UNAVAILABLE,
    // Null is "nothing measured", not "nothing processed": only bills processed since the stamp
    // shipped carry one, so recent bills without it leave the share unanswered.
    hint: 'Collecting data',
    state: TILE_STATE.unavailable
  } : {
    label: 'Auto-attached',
    value: formatPercent(autoAttached),
    hint: figure(summary.stampedSampleSize) === null ? 'Placed without a person' : "Of ".concat(plural(summary.stampedSampleSize, 'bill'), " processed recently"),
    state: TILE_STATE.available
  }];
};

/**
 * The tab counts, from the server rather than from the loaded page.
 *
 * Null when the server has not answered, so the tabs show no counts rather than zeroes, or counts
 * of the one filtered page in hand (LB-6087). A batch-scoped view never asks: it counts its own rows.
 */
export const summaryToTabCounts = summary => {
  if (!summary) return null;
  const all = figure(summary.totalBills);
  const needsReview = figure(summary.needsReviewCount);
  const processed = figure(summary.processedCount);

  // All three or none. A partial answer would blank the badges, and the caller's own count over the
  // rows it holds is a better answer than an empty one.
  return all === null || needsReview === null || processed === null ? null : {
    all,
    needsReview,
    processed
  };
};